import React, { useEffect, useMemo } from "react";

// @TODO: Fix this better
import { hasValue } from "@examspark/common";
import {
  NoResults,
  useQueryGetAllCalendarsForCourse,
  useQueryGetDefaultWorkspaceCalendar,
  useQueryGetUnitLayoutsForCourse,
} from "@examspark/common-app";
import { SelectInput } from "@examspark/common-ui";
import { IconCalendarLine } from "@examspark/common-ui/dist/components/icons";
import { YagDataSource } from "@examspark/customer/tcmpc";
import { Course } from "@examspark/types";

// since we can't just use ID for the value of the select input, we need to
// also take sk into account
const selectValue = (calOrLayout: YagDataSource) => {
  return [calOrLayout?.value.id, calOrLayout?.value.sk].join("/");
};

export const YagDataSourceSelect = (props: {
  course: Course | undefined;
  allowTypes?: Array<NonNullable<YagDataSource>["type"]>;
  value: YagDataSource;
  workspaceId?: string;
  onChange: (value: YagDataSource) => void;
}) => {
  const { value, onChange } = props;

  const allowCalendar = props.allowTypes?.includes("CourseCalendar") ?? true;
  const allowUnitLayout = props.allowTypes?.includes("UnitLayout") ?? true;

  const { data: calendars } = useQueryGetAllCalendarsForCourse(
    allowCalendar ? props.course : undefined,
  );
  const { data: genericYAGResults } = useQueryGetUnitLayoutsForCourse(
    allowUnitLayout ? props.course : undefined,
  );
  const workspaceDefaultCalendarQuery = useQueryGetDefaultWorkspaceCalendar(
    allowCalendar ? props.workspaceId : undefined,
  );

  useEffect(() => {
    if (onChange && !value) {
      if (workspaceDefaultCalendarQuery.data) {
        const defaultCalendar = calendars?.find(
          (cal) =>
            cal.id === workspaceDefaultCalendarQuery.data?.id ||
            cal.calendarId === workspaceDefaultCalendarQuery.data?.id,
        );
        if (defaultCalendar) {
          onChange({
            type: "CourseCalendar",
            value: defaultCalendar,
          });
          return;
        }
      }
      if (genericYAGResults?.length) {
        onChange({
          type: "UnitLayout",
          value: genericYAGResults[0],
        });
      }
    }
  }, [
    workspaceDefaultCalendarQuery.data,
    calendars,
    onChange,
    value,
    genericYAGResults,
  ]);

  const calendarsAndUnitLayouts: YagDataSource[] = [
    ...(calendars ?? []).map((cal) => ({
      type: "CourseCalendar" as const,
      value: cal,
    })),
    ...(genericYAGResults ?? []).map((layout) => ({
      type: "UnitLayout" as const,
      value: layout,
    })),
  ];

  const foundActiveValue = calendarsAndUnitLayouts.find(
    (calOrLayout) => selectValue(calOrLayout) === selectValue(value),
  );

  if (calendarsAndUnitLayouts.length === 0) {
    const message =
      allowCalendar && allowUnitLayout
        ? "There are no calendars or Year-At-A-Glance for this course."
        : allowCalendar
          ? "There are no calendars for this course."
          : allowUnitLayout
            ? "The course author has not created a Year at a Glance for this course."
            : "No calendar data is available.";

    return <NoResults icon={<IconCalendarLine />}>{message}</NoResults>;
  }

  return (
    <CourseCalendarSelectInput
      allValues={calendarsAndUnitLayouts ?? []}
      value={foundActiveValue}
      onChange={(calendar) => {
        props.onChange?.(calendar);
      }}
    />
  );
};

const CourseCalendarSelectInput = ({
  allValues,
  value,
  onChange,
}: {
  allValues: YagDataSource[];
  value: YagDataSource;
  onChange: (cal: YagDataSource) => void;
}) => {
  // separate the YAGs by their data source
  const dataSourceByType = useMemo(() => {
    const _dataSourceByType: Record<string, YagDataSource[]> = {};

    allValues.forEach((calOrLayout) => {
      const type =
        calOrLayout?.type === "CourseCalendar"
          ? "Your Calendars"
          : "Year at a Glance";
      if (!type) return;

      if (!_dataSourceByType[type]) {
        _dataSourceByType[type] = [];
      }
      _dataSourceByType[type].push(calOrLayout);
    });
    return _dataSourceByType;
  }, [allValues]);

  return (
    <SelectInput
      options={Object.entries(dataSourceByType).map(
        ([workspaceId, calOrLayouts]) => ({
          label: workspaceId,
          options: calOrLayouts.filter(hasValue).map((calOrLayout) => {
            const periodLabel =
              calOrLayout.type === "CourseCalendar"
                ? calOrLayout.value.calendar?.gradingPeriodLength === "6_WEEKS"
                  ? " (6 Week)"
                  : " (9 Week)"
                : "";
            const label = (calOrLayout?.value.name ?? "Untitled") + periodLabel;

            return {
              value: selectValue(calOrLayout),
              label,
            };
          }),
        }),
      )}
      value={selectValue(value)}
      onChange={(e) => {
        console.log({ e });

        return onChange(
          allValues.find((val) => selectValue(val) === e?.value) ?? undefined,
        );
      }}
    />
  );
};

export default YagDataSourceSelect;
